import React, { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomSearch from "../../../components/common/CustomSearch";
import CustomButton from '../../../components/common/CustomButton/CustomButton';
import CustomDataGrid from "../../../components/common/CustomDataGrid/CustomDataGrid";
import CustomSwitch from "../../../components/common/CustomSwitch";
import CustomDialog from "../../../components/common/CustomDialog";
import NotificationFormDialog from "./NotificationFormDialog";
import { deleteNotificationTriggerMessagesUrl, getNotificationTriggerMessagesUrl, updateNotificationTriggerMessagesUrl } from "../../../utils/urls";
import axios from "axios";
import { errorToast, successToast } from "../../../utils/toasts";

const Notification = () => {
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(initialConfirmDialog);
  const [isLoading, setIsLoading] = useState(false);

  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchMessage();
  }, []);

  function fetchMessage() {
    setIsLoading(true);
    axios.get(getNotificationTriggerMessagesUrl)
      .then(response => {
        // console.log(response.data);
        
        setNotifications([...response?.data?.data]);
        setIsLoading(false);
      })
      .catch(error => {
        console.error(error.response);
        errorToast(error?.response?.data?.error?.message || "Failed to fetch notifications. Please try again.");
        setIsLoading(false);
      });
  }
  

  const handleToggleNotification = (value, id) => {
    setConfirmDialog({
      open: true,
      type: 'status',
      data: { id, value }
    });
  };

  const handleDeleteClick = (id) => {
    setConfirmDialog({
      open: true,
      type: 'delete',
      data: { id }
    });
  };

  const handleConfirmAction = async () => {
    setIsLoading(true);
    try {
      if (confirmDialog.type === 'status') {
        const { id, value } = confirmDialog.data;
        axios.put(updateNotificationTriggerMessagesUrl, { id, is_active: value }).then(response => {
          successToast(value ? 'Notification enabled successfully' : 'Notification disabled successfully');
          fetchMessage();
          setConfirmDialog({...initialConfirmDialog});
        }).catch(error => {
          console.error(error.response);
          errorToast(error?.response?.data?.error?.message || "Failed to update notification. Please try again.");
          setConfirmDialog({...initialConfirmDialog});
        })
      } else if (confirmDialog.type === 'delete') {
        const { id } = confirmDialog.data;
      
        axios.delete(deleteNotificationTriggerMessagesUrl, {data: { id } }).then(response => {
          successToast('Notification deleted successfully');
          fetchMessage();
          setConfirmDialog({...initialConfirmDialog});
        }).catch(error => {
          console.error(error.response);
          errorToast(error?.response?.data?.error?.message || "Failed to delete notification. Please try again.");
          setConfirmDialog({...initialConfirmDialog});
        })
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
      setConfirmDialog({...initialConfirmDialog});
    }
  };

  const handleEdit = (row) => {
    setEditData(row);
    setIsFormOpen(true);
  };

  const handleAdd = () => {
    setEditData(null);
    setIsFormOpen(true);
  };

  const columns = [
    // {
    //   field: "srNo",
    //   headerName: "#",
    //   width: "80px",
    //   headerClassName: "text-[#213650]",
    //   renderCell: (row) => (
    //     <span className="text-[#282828]">{row.id}</span>
    //   )
    // },
    {
      field: "trigger",
      headerName: "Trigger Notifications",
      width: "250px",
      headerClassName: "text-[#213650]",
      renderCell: (row) => (
        <span className="text-[#7893B0]">{row?.notification_trigger_list_id?.title}</span>
      )
    },
    {
      field: "recipient",
      headerName: "Recipient",
      width: "150px",
      headerClassName: "text-[#213650]",
      renderCell: (row) => (
        <span className="text-[#282828]">{row.recipient}</span>
      )
    },
    {
      field: "title",
      headerName: "Title",
      width: "200px",
      headerClassName: "text-[#213650]",
      renderCell: (row) => (
        <span className="text-[#282828]">{row.title}</span>
      )
    },
    // {
    //   field: "body",
    //   headerName: "Body",
    //   width: "200px",
    //   headerClassName: "text-[#213650]",
    //   renderCell: (row) => (
    //     <span className="text-[#7893B0]">{row.body}</span>
    //   )
    // },
    {
      field: "deliveryMethod",
      headerName: "Delivery Method",
      width: "200px",
      headerClassName: "text-[#213650]",
      renderCell: (row) => (
        <div className="flex gap-2">
          {row?.is_email && 
            <span
              className={`px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium whitespace-nowrap`}
            >
              Email
            </span>
          }
          {row?.is_sms && 
            <span
              className={`px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium`}
            >
              SMS
            </span>
          }
          {row?.is_whatsapp && 
            <span
              className={`px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium`}
            >
              What's App
            </span>
          }
        </div>
      )
    },
    {
      field: "action",
      headerName: "Action",
      width: "150px",
      headerClassName: "text-[#213650]",
      renderCell: (row) => {

        return (
          <div className="flex flex-wrap items-center gap-4">
            <CustomSwitch
              value={row.is_active}
              name={row.id}
              onChange={(value) => {
                handleToggleNotification(value, row.id);
                // logRow();
              }}
            />
            <button 
              className="p-2 hover:bg-gray-100 rounded-full"
              onClick={() => handleEdit(row)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg>
            </button>
            <button 
              className="p-2 hover:bg-gray-100 rounded-full"
              onClick={() => handleDeleteClick(row.id)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-red-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
              </svg>
            </button>
          </div>
        )
      }
    }
  ];

  return (
    <div className="flex flex-1 flex-col bg-gray-50">
      {/* Header */}
      <div className="gap-4 mb-6 flex items-center">
        <button
          className="p-2 border rounded-md"
          onClick={() => {
            navigate("/configurations");
          }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-semibold text-blue-900">Notifications</h1>
      </div>

      {/* Search and Add Button */}
      <div className=" flex justify-between items-center  rounded-lg  mb-6">
        <div className="w-full max-w-md">
          {/* <CustomSearch
            placeholder="Search here..."
            onChange={() => {}}
            className="w-full bg-white"
          /> */}
        </div>
        <CustomButton
          label="Add New Notification"
          variant="contained"
          onClick={handleAdd}
        />
      </div>

      {/* Notification Grid */}
      <div className="flex flex-1 flex-col bg-white rounded-lg shadow-sm">
        <CustomDataGrid
          isLoading={isLoading}
          columns={columns}
          rows={notifications}
          keyId="id"
          perPageSize={10}
        />
      </div>

      {/* Notification Form Dialog */}
      {isFormOpen &&
        <NotificationFormDialog
          open={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditData(null);
          }}
          onSubmit={fetchMessage}
          editData={editData}
        />
      }

      {/* Confirmation Dialog */}
      {(confirmDialog?.open === true) &&
        <CustomDialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({...initialConfirmDialog})}
          title="Are You Sure?"
          titleClassName="text-center"
        >
          <div className="w-[400px] text-center">
            <p className="text-gray-600 text-left border-b pb-4">
              {confirmDialog.type === 'delete' 
                ? "Are you sure you want to delete this notification?"
                : "Are you sure you want to change the status of this notification?"}
            </p>
            <div className="flex justify-between gap-3 mt-4">
              <button
                className="py-2 px-4 bg-gray-200 font-semibold text-base w-[170px] rounded-md"
                onClick={() => setConfirmDialog({...initialConfirmDialog})}
              >
                Cancel
              </button>

              <button
                className="py-2 px-4 bg-blue-500 w-[170px] font-semibold text-base text-white rounded-md disabled:opacity-70"
                onClick={handleConfirmAction}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </CustomDialog>
      }
    </div>
  );
};

export default Notification;


const initialConfirmDialog = {
    open: false,
    type: null, // 'status' or 'delete'
    data: null
  }